import ReactGA, { Tracker, InitializeOptions } from 'react-ga';
import { isFunction, isString, set } from 'lodash';
import { User } from '@fusion/auth';
import { TelemetryPluginWithConfig } from '../types';

export type GaUniversalPluginConfig = {
  trackers: Array<{
    /** Whether to automatically add the user id (if available) to the GA config */
    sendUserId: boolean;

    /** Optional callback for setting additional dimensions */
    dimensions?: (user?: User) => Record<string, string> | undefined;

    /** ReactGA config */
    config: string | Tracker;
  }>;

  /** ReactGA options applied to all trackers */
  trackerDefaults?: InitializeOptions;
};

const name = 'ga-universal';

declare module './Plugins' {
  interface Plugins {
    [name]: { trackerNames: string[]; ReactGA: typeof ReactGA };
  }
}

export type GaUniversalPluginCtx = {
  trackerNames: Array<string>;
  ReactGA: typeof ReactGA;
};

export const GaUniversalPlugin: TelemetryPluginWithConfig<GaUniversalPluginConfig, GaUniversalPluginCtx> = ({
  trackers,
  trackerDefaults = {},
}) => {
  let trackerNames: string[];

  return {
    name,

    init({ user }) {
      const reactGaConfig = trackers
        // Remove trackers where a config has been provided that doesn't include a name
        .filter(({ config }) => {
          if (!isString(config) && !config.gaOptions?.name) {
            console.error(
              '@fusion/reporting: config.gaOptions.name must be provided when using multiple GA trackers. This tracker will be ignored',
            );
            return false;
          }

          return true;
        })
        .map(({ sendUserId, dimensions, config }, idx) => {
          let trackerConfig: Tracker;

          // If the config is a string, turn it into an object
          if (isString(config)) {
            trackerConfig = {
              trackingId: config,
              gaOptions: {
                name: `t${idx}`,
              },
            };
          } else {
            trackerConfig = config;
          }

          // Automatically add the user id to the tracker config, if available / allowed
          if (user && user.id && sendUserId) {
            set(trackerConfig, 'gaOptions.userId', user.id);
          }

          return { dimensions, trackerConfig };
        });

      // Get the tracker names so they can be added to the context.
      // This is so they can be used anywhere in apps, such as when calling ReactGA.event()
      trackerNames = reactGaConfig.reduce((prev, curr) => {
        return [
          ...prev,
          ...(curr.trackerConfig.gaOptions && curr.trackerConfig.gaOptions.name
            ? [curr.trackerConfig.gaOptions.name]
            : []),
        ];
      }, []);

      ReactGA.initialize(
        reactGaConfig.map(({ trackerConfig }) => trackerConfig),
        trackerDefaults,
      );

      // Run any callbacks to set additional dimensions
      reactGaConfig.forEach(({ dimensions: getDimensions, trackerConfig }) => {
        if (isFunction(getDimensions)) {
          const dimensions = getDimensions(user);
          if (dimensions) {
            ReactGA.set(dimensions, trackerConfig.gaOptions?.name ? [trackerConfig.gaOptions.name] : undefined);
          }
        }
      });

      return {
        trackerNames,
        ReactGA,
      };
    },

    onRouteChange({ url, title }) {
      ReactGA.pageview(url, trackerNames, title);
    },
  };
};
